import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Edit, Trash2, CheckCircle, XCircle, Filter } from 'lucide-react';
import Layout from '../components/Layout';
import SubscriptionForm from '../components/SubscriptionForm';
import { subscriptionService } from '../services/subscriptionService';
import { useAuth } from '../context/AuthContext';
import { clubService } from '../services/clubService';

const Subscriptions = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedPaid, setSelectedPaid] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');

  const isSuperAdmin = user?.role?.name === 'super_admin';
  const isAdmin = user?.role?.name === 'admin';

  // Generar lista de años (últimos 10 años y próximos 2)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 2; i++) {
    years.push(i);
  }

  useEffect(() => {
    loadSubscriptions();
    if (isSuperAdmin) {
      loadClubs();
    }
  }, [selectedYear, selectedPaid, selectedClub]);

  useEffect(() => {
    filterSubscriptions();
  }, [subscriptions, searchTerm, selectedYear, selectedPaid]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedYear && selectedYear !== 'all') {
        params.year = selectedYear;
      }
      
      if (selectedPaid !== 'all') {
        params.paid = selectedPaid === 'paid';
      }

      if (isSuperAdmin && selectedClub && selectedClub !== 'all') {
        params.club_id = selectedClub;
      }

      const response = await subscriptionService.getSubscriptions(params);
      const subscriptionsList = response.data || response;
      setSubscriptions(Array.isArray(subscriptionsList) ? subscriptionsList : []);
    } catch (error) {
      console.error('Error al cargar inscripciones:', error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClubs = async () => {
    try {
      const response = await clubService.getClubs();
      const clubsList = response.data || response;
      setClubs(Array.isArray(clubsList) ? clubsList : []);
    } catch (error) {
      console.error('Error al cargar clubs:', error);
      setClubs([]);
    }
  };

  const filterSubscriptions = () => {
    let filtered = subscriptions;

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(sub => {
        const userName = `${sub.user?.name || ''} ${sub.user?.surname || ''}`.toLowerCase();
        const rateName = sub.rate?.name?.toLowerCase() || '';
        const clubName = sub.rate?.club?.name?.toLowerCase() || '';
        return userName.includes(searchTerm.toLowerCase()) ||
               rateName.includes(searchTerm.toLowerCase()) ||
               clubName.includes(searchTerm.toLowerCase());
      });
    }

    setFilteredSubscriptions(filtered);
  };

  const handleCreateSubscription = () => {
    setEditingSubscription(null);
    setShowForm(true);
  };

  const handleEditSubscription = (subscription) => {
    setEditingSubscription(subscription);
    setShowForm(true);
  };

  const handleDeleteClick = (subscription) => {
    setSubscriptionToDelete(subscription);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSubscriptionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!subscriptionToDelete) return;
    
    setDeleting(true);
    try {
      await subscriptionService.deleteSubscription(subscriptionToDelete.id);
      await loadSubscriptions();
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error eliminando inscripción:', error);
      alert('Error al eliminar la inscripción. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = async () => {
    await loadSubscriptions();
    setShowForm(false);
    setEditingSubscription(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSubscription(null);
  };

  const togglePaid = async (subscription) => {
    try {
      await subscriptionService.updateSubscription(subscription.id, {
        paid: !subscription.paid
      });
      await loadSubscriptions();
    } catch (error) {
      console.error('Error actualizando estado de pago:', error);
      alert('Error al actualizar el estado de pago. Inténtalo de nuevo.');
    }
  };

  return (
    <Layout title="Inscripciones" description="Control de inscripciones anuales de los socios">
      {/* Header con filtros y botón de crear */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por socio, tarifa o club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los años</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <select
              value={selectedPaid}
              onChange={(e) => setSelectedPaid(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas</option>
              <option value="paid">Pagadas</option>
              <option value="unpaid">No pagadas</option>
            </select>

            {isSuperAdmin && (
              <select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos los clubs</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>{club.name}</option>
                ))}
              </select>
            )}

            <button
              onClick={handleCreateSubscription}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus size={20} className="mr-2" />
              Nueva Inscripción
            </button>
          </div>
        </div>
      </div>

      {/* Listado de inscripciones */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Socio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarifa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Club
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Año
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.user?.name} {subscription.user?.surname || ''}
                      </div>
                      <div className="text-sm text-gray-500">
                        {subscription.user?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{subscription.rate?.name}</div>
                      <div className="text-sm text-gray-500">
                        {subscription.rate?.price} {subscription.rate?.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {subscription.rate?.club?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {subscription.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => togglePaid(subscription)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          subscription.paid
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                        title={subscription.paid ? 'Marcar como no pagada' : 'Marcar como pagada'}
                      >
                        {subscription.paid ? (
                          <>
                            <CheckCircle size={14} className="mr-1" />
                            Pagada
                          </>
                        ) : (
                          <>
                            <XCircle size={14} className="mr-1" />
                            No pagada
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditSubscription(subscription)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(subscription)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mensaje si no hay inscripciones */}
          {filteredSubscriptions.length === 0 && (
            <div className="text-center py-12">
              <FileText size={64} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron inscripciones</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedYear !== 'all' || selectedPaid !== 'all'
                  ? 'No hay inscripciones que coincidan con los filtros aplicados.'
                  : 'Aún no hay inscripciones registradas.'
                }
              </p>
              <button
                onClick={handleCreateSubscription}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Crear Primera Inscripción
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal del formulario */}
      <SubscriptionForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        subscriptionData={editingSubscription}
        isEditing={!!editingSubscription}
      />

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && subscriptionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la inscripción de <strong>{subscriptionToDelete.user?.name} {subscriptionToDelete.user?.surname || ''}</strong> para el año <strong>{subscriptionToDelete.year}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Subscriptions;
